import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, message, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined, PauseCircleOutlined, NotificationOutlined, FileSearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { liveFollowAPI, brokerAPI, tradeAPI, notificationAPI, authAPI } from '../services/api';

const brokerTypeOptions = [
  { value: 'longport', label: '长桥 longport' },
  { value: 'ib', label: 'IB' },
  { value: 'tiger', label: '老虎 tiger' },
  { value: 'futu', label: 'FUTU' },
  { value: 'moomoo', label: 'MOOMOO' },
];

const brokerApiKeyByType = {
  futu: 'futu',
  moomoo: 'moomoo',
  longport: 'long_port',
  tiger: 'tiger',
  ib: 'ib',
};

const fundLimitTypeOptions = [
  { value: null, label: '不限制' },
  { value: 'ratio', label: '按比例（0.01~1.2）' },
  { value: 'usd', label: '固定上限（USD，>=1500）' },
];

const LiveFollows = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startingSyncId, setStartingSyncId] = useState(null);
  const [startingNotifyId, setStartingNotifyId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [ignoredSymbols, setIgnoredSymbols] = useState([]);
  const [ignoredLoading, setIgnoredLoading] = useState(false);
  const [ignoreModalVisible, setIgnoreModalVisible] = useState(false);
  const [ignoreEditingRecord, setIgnoreEditingRecord] = useState(null);
  const [ignoreSubmitting, setIgnoreSubmitting] = useState(false);
  const [ignoreForm] = Form.useForm();

  const [logModalVisible, setLogModalVisible] = useState(false);
  const [logFollow, setLogFollow] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  const [logPagination, setLogPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [logActionFilter, setLogActionFilter] = useState(undefined);

  const brokerType = Form.useWatch('broker_type', form);
  const fundLimitType = Form.useWatch('fund_limit_type', form);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await liveFollowAPI.list();
      setData(resp.data || []);
    } catch (e) {
      message.error('获取实盘跟随失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchIgnoredSymbols = async () => {
    setIgnoredLoading(true);
    try {
      const resp = await liveFollowAPI.listIgnoredSymbols();
      setIgnoredSymbols(resp.data || []);
    } catch (e) {
      message.error('获取忽略股票配置失败');
    } finally {
      setIgnoredLoading(false);
    }
  };

  const fetchOperationLogs = async (record, page = 1, pageSize = 10, action = logActionFilter) => {
    if (!record?.id) return;
    setLogLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params = { limit: pageSize, offset };
      if (action) params.action = action;
      const resp = await liveFollowAPI.getOperationLogs(record.id, params);
      const payload = resp?.data || {};
      setLogData(payload.items || []);
      setLogPagination({
        current: page,
        pageSize,
        total: Number(payload.total || 0),
      });
    } catch (e) {
      message.error('获取操作日志失败');
      setLogData([]);
      setLogPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLogLoading(false);
    }
  };

  const handleOpenLogs = (record) => {
    setLogFollow(record);
    setLogActionFilter(undefined);
    setLogModalVisible(true);
    fetchOperationLogs(record, 1, logPagination.pageSize, undefined);
  };

  const logColumns = [
    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: (v) => (v ? moment(v).format('YYYY-MM-DD HH:mm:ss') : '-') },
    {
      title: '动作',
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (v) => {
        const colorMap = {
          create: 'green',
          update: 'blue',
          delete: 'red',
          start_sync: 'cyan',
          stop_sync: 'orange',
          start_notify: 'purple',
          stop_notify: 'magenta',
        };
        return <Tag color={colorMap[v] || 'default'}>{v || '-'}</Tag>;
      },
    },
    { title: '操作人', dataIndex: 'operator_username', key: 'operator_username', width: 140, render: (v, r) => v || r?.operator_user_id || '-' },
    { title: '摘要', dataIndex: 'summary', key: 'summary' },
    {
      title: '变更详情',
      dataIndex: 'changed_fields',
      key: 'changed_fields',
      width: 420,
      render: (v, record) => {
        const changed = v && typeof v === 'object' ? v : null;
        const entries = changed ? Object.entries(changed) : [];

        const formatVal = (val) => {
          if (val === null || val === undefined) return '∅';
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        };

        if (entries.length > 0) {
          return (
            <div style={{ maxHeight: 140, overflowY: 'auto', fontSize: 12, lineHeight: 1.5 }}>
              {entries.map(([field, diff]) => (
                <div key={field} style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>{field}</span>
                  <span style={{ color: '#999', margin: '0 6px' }}>:</span>
                  <span style={{ color: '#cf1322' }}>{formatVal(diff?.old)}</span>
                  <span style={{ margin: '0 6px' }}>→</span>
                  <span style={{ color: '#389e0d' }}>{formatVal(diff?.new)}</span>
                </div>
              ))}
            </div>
          );
        }

        if (record?.payload && typeof record.payload === 'object') {
          return (
            <div style={{ maxHeight: 140, overflowY: 'auto', fontSize: 12, lineHeight: 1.5 }}>
              {Object.entries(record.payload).map(([field, value]) => (
                <div key={field} style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>{field}</span>
                  <span style={{ color: '#999', margin: '0 6px' }}>:</span>
                  <span>{formatVal(value)}</span>
                </div>
              ))}
            </div>
          );
        }

        return '-';
      },
    },
  ];

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const resp = await authAPI.users();
      setUsers(resp.data || []);
    } catch (e) {
      message.error('获取用户列表失败');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleStopSync = async (record) => {
    if (!record?.id) return;
    setStartingSyncId(record.id);
    try {
      const res = await liveFollowAPI.stopSync(record.id);
      const data = res?.data ?? {};
      if (data?.ok) {
        message.success('已关闭同步');
      } else {
        message.error('关闭同步失败');
      }
      Modal.info({
        title: '关闭同步返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
      fetchData();
    } catch (e) {
      const data = e?.response?.data ?? { ok: false, error: e?.message || 'request failed' };
      message.error('关闭同步失败');
      Modal.info({
        title: '关闭同步返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } finally {
      setStartingSyncId(null);
    }
  };

  const handleStopNotify = async (record) => {
    if (!record?.id) return;
    setStartingNotifyId(record.id);
    try {
      const res = await liveFollowAPI.stopNotify(record.id);
      const data = res?.data ?? {};
      if (data?.ok) {
        message.success('已关闭通知');
      } else {
        message.error('关闭通知失败');
      }
      Modal.info({
        title: '关闭通知返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
      fetchData();
    } catch (e) {
      const data = e?.response?.data ?? { ok: false, error: e?.message || 'request failed' };
      message.error('关闭通知失败');
      Modal.info({
        title: '关闭通知返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } finally {
      setStartingNotifyId(null);
    }
  };

  const fetchTopics = async () => {
    setTopicsLoading(true);
    try {
      const resp = await tradeAPI.getTopics();
      setTopics(resp.data || []);
    } catch (e) {
      message.error('获取话题失败');
    } finally {
      setTopicsLoading(false);
    }
  };

  const fetchWebhooks = async () => {
    setWebhooksLoading(true);
    try {
      const resp = await notificationAPI.getWebhooks();
      setWebhooks(resp.data || []);
    } catch (e) {
      message.error('获取webhook失败');
    } finally {
      setWebhooksLoading(false);
    }
  };

  const fetchAllAccounts = async () => {
    const brokerTypes = ['longport', 'ib', 'tiger', 'futu', 'moomoo'];
    const accountPromises = brokerTypes.map((type) => {
      const k = brokerApiKeyByType[type];
      return brokerAPI.list(k).catch(() => ({ data: [] }));
    });
    const accountResponses = await Promise.all(accountPromises);
    const allAccounts = accountResponses.flatMap((resp) => (Array.isArray(resp?.data) ? resp.data : []));
    setAccounts(allAccounts);
  };

  const fetchAccounts = async (bt) => {
    // 编辑模式下根据券商类型加载账户（用于下拉选择）
    const k = brokerApiKeyByType[(bt || '').toLowerCase()];
    if (!k) {
      setAccounts([]);
      return;
    }

    setAccountsLoading(true);
    try {
      const resp = await brokerAPI.list(k);
      setAccounts(resp.data || []);
    } catch (e) {
      message.error('获取券商账户失败');
    } finally {
      setAccountsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTopics();
    fetchWebhooks();
    fetchUsers();
    fetchIgnoredSymbols();
    fetchAllAccounts(); // 加载所有账户用于列表显示
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (brokerType) {
      fetchAccounts(brokerType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerType]);

  const topicNameById = useMemo(() => new Map((topics || []).map((t) => [t.id, t.name])), [topics]);
  const webhookNameById = useMemo(() => new Map((webhooks || []).map((w) => [w.id, w.name || `Webhook ${w.id}`])), [webhooks]);
  const accountNameById = useMemo(() => new Map((accounts || []).map((a) => [a.id, a.name || String(a.id)])), [accounts]);
  const usernameById = useMemo(() => new Map((users || []).map((u) => [u.id, u.username || String(u.id)])), [users]);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: '绑定用户',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 180,
      render: (v) => {
        if (!v) return '';
        const n = usernameById.get(v);
        return n ? `${v} - ${n}` : String(v);
      },
    },
    { title: '别名', dataIndex: 'alias', key: 'alias', width: 160 },
    { title: '券商', dataIndex: 'broker_type', key: 'broker_type', width: 120 },
    {
      title: '账户',
      dataIndex: 'broker_account_id',
      key: 'broker_account_id',
      width: 200,
      render: (v, r) => {
        // 先尝试同时匹配 accountId 和 brokerType
        let account = accounts.find((a) => a?.id === v && (a?.broker_type || '').toLowerCase() === (r?.broker_type || '').toLowerCase());
        // 如果找不到，回退到只按 accountId 匹配
        if (!account) {
          account = accounts.find((a) => a?.id === v);
        }
        const n = account?.name;
        return n ? `${v} - ${n}` : String(v ?? '');
      },
    },
    {
      title: '话题',
      dataIndex: 'topic_id',
      key: 'topic_id',
      width: 220,
      render: (v) => {
        const n = topicNameById.get(v);
        return n ? `${v} - ${n}` : String(v ?? '');
      },
    },
    { title: '启用', dataIndex: 'enabled', key: 'enabled', width: 90, render: (v) => (v ? '是' : '否') },
    { title: '允许误差', dataIndex: 'allowed_error', key: 'allowed_error', width: 100 },
    {
      title: '资金限制',
      dataIndex: 'fund_limit_type',
      key: 'fund_limit_type',
      width: 200,
      render: (_, r) => {
        const t = (r.fund_limit_type || '').toLowerCase();
        const v = r.fund_limit_value;
        if (!t || v === null || v === undefined || v === '') return '';
        if (t === 'ratio') return `比例 ${v}`;
        if (t === 'usd') return `USD ${v}`;
        return `${t} ${v}`;
      },
    },
    { title: '通知', dataIndex: 'notification_enabled', key: 'notification_enabled', width: 90, render: (v) => (v ? '是' : '否') },
    {
      title: 'Webhook',
      dataIndex: 'webhook_id',
      key: 'webhook_id',
      width: 200,
      render: (v) => {
        if (!v) return '';
        const n = webhookNameById.get(v);
        return n ? `${v} - ${n}` : String(v);
      },
    },
    { title: '最后同步', dataIndex: 'last_sync_at', key: 'last_sync_at', width: 160, render: (date) => (date ? moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : '-') },
    { title: '同步状态', dataIndex: 'last_sync_status', key: 'last_sync_status', width: 120 },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 360,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Button type="link" icon={record.enabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />} loading={startingSyncId === record.id} onClick={() => (record.enabled ? handleStopSync(record) : handleStartSync(record))}>
              {record.enabled ? '关闭同步' : '启动同步'}
            </Button>
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              编辑
            </Button>
          </Space>
          <Space>
            <Button type="link" icon={<NotificationOutlined />} loading={startingNotifyId === record.id} onClick={() => (record.notification_enabled ? handleStopNotify(record) : handleStartNotify(record))}>
              {record.notification_enabled ? '关闭通知' : '启动通知'}
            </Button>

            <Button type="link" icon={<FileSearchOutlined />} onClick={() => handleOpenLogs(record)}>
              日志
            </Button>

            <Button type="link" danger icon={<DeleteOutlined />} loading={deletingId === record.id} onClick={() => handleConfirmDelete(record)}>
              删除
            </Button>
          </Space>
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ user_id: null, enabled: false, allowed_error: 0.03, notification_enabled: false, fund_limit_type: 'ratio', fund_limit_value: 0.95 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      await liveFollowAPI.delete(record.id);
      message.success('删除成功');
      fetchData();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const handleConfirmDelete = (record) => {
    if (!record?.id) return;
    const displayName = record.alias ? `${record.alias}` : `ID=${record.id}`;
    Modal.confirm({
      title: '确认删除',
      content: (
        <div>
          <div>{`确定要删除实盘跟随“${displayName}”吗？`}</div>
          <div style={{ color: 'red', marginTop: 8 }}>此操作无法撤销，删除后将永久失去该实盘跟随及其所有相关数据。</div>
        </div>
      ),
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletingId(record.id);
        try {
          await handleDelete(record);
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handleStartSync = async (record) => {
    if (!record?.id) return;
    setStartingSyncId(record.id);
    try {
      const res = await liveFollowAPI.startSync(record.id);
      const data = res?.data ?? {};
      if (data?.ok) {
        message.success('已启动同步');
      } else {
        message.error('启动同步失败');
      }
      Modal.info({
        title: '启动同步返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
      fetchData();
    } catch (e) {
      const data = e?.response?.data ?? { ok: false, error: e?.message || 'request failed' };
      message.error('启动同步失败');
      Modal.info({
        title: '启动同步返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } finally {
      setStartingSyncId(null);
    }
  };

  const handleStartNotify = async (record) => {
    if (!record?.id) return;
    setStartingNotifyId(record.id);
    try {
      const res = await liveFollowAPI.startNotify(record.id);
      const data = res?.data ?? {};
      if (data?.ok) {
        message.success('已启动通知');
      } else {
        message.error('启动通知失败');
      }
      Modal.info({
        title: '启动通知返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
      fetchData();
    } catch (e) {
      const data = e?.response?.data ?? { ok: false, error: e?.message || 'request failed' };
      message.error('启动通知失败');
      Modal.info({
        title: '启动通知返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>,
      });
    } finally {
      setStartingNotifyId(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const title = editingRecord ? '确认更新' : '确认创建';
      Modal.confirm({
        title,
        content: '确定要提交本次变更吗？',
        okText: '确认提交',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
          if (editingRecord) {
            await liveFollowAPI.update(editingRecord.id, values);
            message.success('更新成功');
          } else {
            await liveFollowAPI.create(values);
            message.success('创建成功');
          }
          setModalVisible(false);
          await fetchData();
        },
      });
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleCreateIgnore = () => {
    setIgnoreEditingRecord(null);
    ignoreForm.resetFields();
    ignoreForm.setFieldsValue({ broker_type: 'ib', enabled: true });
    setIgnoreModalVisible(true);
  };

  const handleEditIgnore = (record) => {
    setIgnoreEditingRecord(record);
    ignoreForm.setFieldsValue({
      broker_type: record?.broker_type || 'ib',
      symbol: record?.symbol || '',
      enabled: !!record?.enabled,
      remark: record?.remark || '',
    });
    setIgnoreModalVisible(true);
  };

  const handleSubmitIgnore = async () => {
    try {
      const values = await ignoreForm.validateFields();
      const payload = {
        broker_type: values.broker_type,
        symbol: String(values.symbol || '')
          .trim()
          .toUpperCase(),
        enabled: !!values.enabled,
        remark: values.remark,
      };
      setIgnoreSubmitting(true);
      if (ignoreEditingRecord?.id) {
        await liveFollowAPI.updateIgnoredSymbol(ignoreEditingRecord.id, payload);
        message.success('忽略配置更新成功');
      } else {
        await liveFollowAPI.createIgnoredSymbol(payload);
        message.success('忽略配置创建成功');
      }
      setIgnoreModalVisible(false);
      fetchIgnoredSymbols();
    } catch (e) {
      if (!e?.errorFields) {
        message.error(e?.response?.data?.detail || '保存忽略配置失败');
      }
    } finally {
      setIgnoreSubmitting(false);
    }
  };

  const handleToggleIgnoreEnabled = async (record, checked) => {
    if (!record?.id) return;
    try {
      await liveFollowAPI.updateIgnoredSymbol(record.id, { enabled: !!checked });
      message.success('状态已更新');
      fetchIgnoredSymbols();
    } catch (e) {
      message.error(e?.response?.data?.detail || '更新状态失败');
    }
  };

  const handleDeleteIgnore = (record) => {
    if (!record?.id) return;
    Modal.confirm({
      title: '确认删除',
      content: `确定删除忽略配置 ${record.broker_type}:${record.symbol} 吗？`,
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await liveFollowAPI.deleteIgnoredSymbol(record.id);
          message.success('删除成功');
          fetchIgnoredSymbols();
        } catch (e) {
          message.error(e?.response?.data?.detail || '删除失败');
        }
      },
    });
  };

  const topicOptions = (topics || []).map((t) => ({ value: t.id, label: `${t.id} - ${t.name}` }));
  const webhookOptions = (webhooks || []).map((w) => ({ value: w.id, label: w.name || `Webhook ${w.id}` }));
  const accountOptions = (accounts || []).map((a) => ({ value: a.id, label: `${a.id} - ${a.name || a.id}` }));
  const userOptions = (users || []).map((u) => ({ value: u.id, label: `${u.id} - ${u.username}` }));

  const ignoreColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '券商', dataIndex: 'broker_type', key: 'broker_type', width: 120 },
    { title: '股票代码', dataIndex: 'symbol', key: 'symbol', width: 140 },
    {
      title: '启用',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (v, r) => <Switch checked={!!v} onChange={(checked) => handleToggleIgnoreEnabled(r, checked)} />,
    },
    { title: '备注', dataIndex: 'remark', key: 'remark' },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => handleEditIgnore(record)}>
            编辑
          </Button>
          <Button type="link" danger onClick={() => handleDeleteIgnore(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const fundLimitValueRules = () => {
    if (!fundLimitType) return [];
    if (String(fundLimitType).toLowerCase() === 'ratio') {
      return [{ required: true, type: 'number', min: 0.01, max: 1.2, message: '比例范围 0.01~1.2' }];
    }
    if (String(fundLimitType).toLowerCase() === 'usd') {
      return [{ required: true, type: 'number', min: 1500, message: 'USD 下限 1500' }];
    }
    return [];
  };

  return (
    <div>
      <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} style={{ marginBottom: 16 }}>
        新增实盘跟随
      </Button>

      <Table columns={columns} dataSource={data} rowKey="id" loading={loading} scroll={{ x: 1400 }} />

      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>忽略股票配置</h3>
          <Button type="primary" onClick={handleCreateIgnore}>
            新增忽略股票
          </Button>
        </div>
        <Table columns={ignoreColumns} dataSource={ignoredSymbols} rowKey="id" loading={ignoredLoading} pagination={{ pageSize: 10, showSizeChanger: true }} />
      </div>

      <Modal title={`操作日志 - ${logFollow?.alias || `实盘 ${logFollow?.id || ''}`}`} open={logModalVisible} onCancel={() => setLogModalVisible(false)} footer={null} width={980}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <Select
            allowClear
            placeholder="按动作筛选"
            value={logActionFilter}
            style={{ width: 260 }}
            options={[
              { value: 'create', label: 'create' },
              { value: 'update', label: 'update' },
              { value: 'delete', label: 'delete' },
              { value: 'start_sync', label: 'start_sync' },
              { value: 'stop_sync', label: 'stop_sync' },
              { value: 'start_notify', label: 'start_notify' },
              { value: 'stop_notify', label: 'stop_notify' },
            ]}
            onChange={(v) => {
              setLogActionFilter(v);
              if (logFollow?.id) {
                fetchOperationLogs(logFollow, 1, logPagination.pageSize, v);
              }
            }}
          />
          <Button onClick={() => logFollow?.id && fetchOperationLogs(logFollow, logPagination.current, logPagination.pageSize, logActionFilter)}>刷新</Button>
        </div>

        <Table
          rowKey="id"
          columns={logColumns}
          dataSource={logData}
          loading={logLoading}
          size="small"
          pagination={{
            current: logPagination.current,
            pageSize: logPagination.pageSize,
            total: logPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => {
              if (logFollow?.id) {
                fetchOperationLogs(logFollow, page, pageSize, logActionFilter);
              }
            },
          }}
        />
      </Modal>

      <Modal title={ignoreEditingRecord ? '编辑忽略股票' : '新增忽略股票'} open={ignoreModalVisible} onOk={handleSubmitIgnore} confirmLoading={ignoreSubmitting} onCancel={() => setIgnoreModalVisible(false)} destroyOnClose>
        <Form form={ignoreForm} layout="vertical">
          <Form.Item name="broker_type" label="券商" rules={[{ required: true, message: '请选择券商' }]}>
            <Select
              options={[
                { value: 'ib', label: 'IB' },
                { value: 'longport', label: 'LongPort' },
                { value: 'all', label: '全部券商' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="symbol"
            label="股票代码"
            rules={[
              { required: true, message: '请输入股票代码' },
              { max: 32, message: '最多32字符' },
            ]}
          >
            <Input placeholder="例如：IBKR" />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="remark" label="备注" rules={[{ max: 255, message: '最多255字符' }]}>
            <Input placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={editingRecord ? '编辑实盘跟随' : '新增实盘跟随'} open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="user_id" label="绑定用户" rules={[{ required: true, message: '请选择绑定用户' }]}>
            <Select allowClear showSearch optionFilterProp="label" loading={usersLoading} options={userOptions} placeholder="请选择用户" />
          </Form.Item>

          <Form.Item name="alias" label="别名">
            <Input />
          </Form.Item>

          <Form.Item name="broker_type" label="券商" rules={[{ required: true, message: '请选择券商' }]}>
            <Select options={brokerTypeOptions} placeholder="请选择券商" />
          </Form.Item>

          <Form.Item name="broker_account_id" label="券商账户" rules={[{ required: true, message: '请选择券商账户' }]}>
            <Select showSearch optionFilterProp="label" loading={accountsLoading} options={accountOptions} placeholder="请选择账户" />
          </Form.Item>

          <Form.Item name="topic_id" label="跟随话题" rules={[{ required: true, message: '请选择话题' }]}>
            <Select showSearch optionFilterProp="label" loading={topicsLoading} options={topicOptions} placeholder="请选择话题" />
          </Form.Item>

          <Form.Item name="enabled" label="同步启用" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="allowed_error" label="允许误差" rules={[{ required: true, type: 'number', min: 0, max: 1 }]}>
            <InputNumber style={{ width: '100%' }} step={0.01} min={0} max={1} />
          </Form.Item>

          <Form.Item name="fund_limit_type" label="资金限制类型">
            <Select
              allowClear
              options={fundLimitTypeOptions}
              placeholder="不限制 / 按比例 / 固定上限"
              onChange={(v) => {
                if (!v) {
                  form.setFieldsValue({ fund_limit_value: null });
                }
              }}
            />
          </Form.Item>

          <Form.Item name="fund_limit_value" label="资金限制值" rules={fundLimitValueRules()}>
            <InputNumber style={{ width: '100%' }} step={String(fundLimitType || '').toLowerCase() === 'ratio' ? 0.01 : 100} min={String(fundLimitType || '').toLowerCase() === 'ratio' ? 0.01 : 0} placeholder={String(fundLimitType || '').toLowerCase() === 'ratio' ? '例如 0.3' : '例如 50000'} disabled={!fundLimitType} />
          </Form.Item>

          <Form.Item name="notification_enabled" label="启用通知" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="webhook_id" label="Webhook">
            <Select allowClear showSearch optionFilterProp="label" loading={webhooksLoading} options={webhookOptions} placeholder="请选择webhook" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LiveFollows;
